import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SENSORY = '#2493A2';
const INTEGRATION = '#57BDA2';

interface Phoneme {
  x: number;
  width: number;
  label: string;
  color: string;
}

interface WordToken {
  x: number;
  label: string;
}

export function WaveformToWord() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const height = 160;
    const midY = height / 2;

    // --- Phase 1: Draw waveform ---
    const wavePoints = d3.range(0, width, 1).map((x) => {
      const y =
        midY +
        Math.sin(x * 0.08) * 18 +
        Math.sin(x * 0.22) * 8 +
        Math.sin(x * 0.55) * 4 +
        (Math.random() - 0.5) * 6;
      return [x, y] as [number, number];
    });

    const line = d3
      .line<[number, number]>()
      .x((d) => d[0])
      .y((d) => d[1])
      .curve(d3.curveBasis);

    const waveGroup = svg.append('g').attr('class', 'waveform');

    const wavePath = waveGroup
      .append('path')
      .datum(wavePoints)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', SENSORY)
      .attr('stroke-width', 1.5)
      .attr('opacity', 0.9);

    // Animate draw-in
    const totalLength = (wavePath.node() as SVGPathElement).getTotalLength();
    wavePath
      .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
      .attr('stroke-dashoffset', totalLength)
      .transition()
      .duration(600)
      .ease(d3.easeLinear)
      .attr('stroke-dashoffset', 0);

    // --- Phase 2: Phoneme blocks ---
    const phonemes: Phoneme[] = [
      { x: 15, width: 42, label: 'hh', color: SENSORY },
      { x: 60, width: 30, label: 'eh', color: INTEGRATION },
      { x: 93, width: 35, label: 'l', color: SENSORY },
      { x: 131, width: 30, label: 'oh', color: INTEGRATION },
      { x: 170, width: 38, label: 'd', color: SENSORY },
      { x: 212, width: 32, label: 'ey', color: INTEGRATION },
      { x: 248, width: 38, label: 'n', color: SENSORY },
      { x: 290, width: 20, label: 'jh', color: INTEGRATION },
    ];

    const phonemeGroup = svg
      .append('g')
      .attr('class', 'phonemes')
      .attr('opacity', 0);

    phonemeGroup
      .selectAll('rect')
      .data(phonemes)
      .enter()
      .append('rect')
      .attr('x', (d) => d.x)
      .attr('y', midY - 18)
      .attr('width', (d) => d.width)
      .attr('height', 36)
      .attr('rx', 4)
      .attr('fill', (d) => d.color)
      .attr('opacity', 0.25);

    phonemeGroup
      .selectAll('text')
      .data(phonemes)
      .enter()
      .append('text')
      .attr('x', (d) => d.x + d.width / 2)
      .attr('y', midY + 4)
      .attr('text-anchor', 'middle')
      .attr('font-size', 9)
      .attr('font-weight', 500)
      .attr('class', 'viz-text-muted')
      .text((d) => d.label);

    // --- Phase 3: Word tokens ---
    const words: WordToken[] = [
      { x: 72, label: 'hello' },
      { x: 195, label: 'danger' },
      { x: 280, label: 'friend' },
    ];

    const wordGroup = svg
      .append('g')
      .attr('class', 'words')
      .attr('opacity', 0);

    wordGroup
      .selectAll('rect')
      .data(words)
      .enter()
      .append('rect')
      .attr('x', (d) => d.x - 30)
      .attr('y', midY - 14)
      .attr('width', 60)
      .attr('height', 28)
      .attr('rx', 6)
      .attr('fill', INTEGRATION)
      .attr('opacity', 0.2);

    wordGroup
      .selectAll('text')
      .data(words)
      .enter()
      .append('text')
      .attr('x', (d) => d.x)
      .attr('y', midY + 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', 700)
      .attr('class', 'viz-text')
      .text((d) => d.label);

    // --- Sequenced transitions ---
    const t1 = d3.timeout(() => {
      waveGroup
        .transition()
        .duration(400)
        .attr('opacity', 0.3);

      phonemeGroup
        .transition()
        .duration(500)
        .attr('opacity', 1);
    }, 1000);

    const t2 = d3.timeout(() => {
      phonemeGroup
        .transition()
        .duration(400)
        .attr('opacity', 0.15);

      waveGroup
        .transition()
        .duration(300)
        .attr('opacity', 0)
        .remove();

      wordGroup
        .transition()
        .duration(500)
        .attr('opacity', 1);
    }, 2200);

    return () => {
      t1.stop();
      t2.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-ear-listen" /> Hearing &mdash; Sound to Meaning
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 160" />
      <p className="viz-container__caption">
        Interpretation happens before you notice it.
      </p>
    </div>
  );
}
